import IMask, {createMask} from 'imask';
import type {FactoryOpts} from 'imask';

export const numberMaskOption = {
  mask: 'num',
  blocks: {
    num: {
      mask: Number,
      scale: 0,
      signed: false,
      thousandsSeparator: ',',
      padFractionalZeros: true,
      overwrite: true,
    },
  },
};

export const integerMaskOption: FactoryOpts = {
  mask: 'num',
  blocks: {
    num: {
      ...numberMaskOption.blocks.num,
      scale: 0,
    },
  },
};

export const integerMask = (number: number | string): string => {
  if (typeof number === 'number') {
    number = String(number);
  }

  return IMask.pipe(number, integerMaskOption);
};

interface DollarMaskOptions {
  currencySymbol?: string;
  scale?: number;
}

export const dollarMaskOption = (options?: DollarMaskOptions): FactoryOpts => ({
  mask: `${options?.currencySymbol || '$'}num`,
  blocks: {
    num: {
      mask: Number,
      scale: options?.scale || 2,
      thousandsSeparator: ',',
      padFractionalZeros: true,
      normalizeZeros: true,
      radix: '.',
      mapToRadix: ['.'],
      overwrite: true,
    },
  },
});

export const priceMaskOption: FactoryOpts = dollarMaskOption({
  currencySymbol: '',
});

export const dollarMask = (
  number: number | string,
  currencySymbol = '$'
): string => {
  if (typeof number === 'number') {
    number = String(number);
  }

  const [, decimalPart] = number.split('.');
  let scale = 2;
  if (decimalPart && decimalPart.length > 2) scale = 3; // in case the number has more than 2 decimal places we need to adjust the scale

  const options: DollarMaskOptions = {
    scale,
    currencySymbol,
  };

  return IMask.pipe(number, dollarMaskOption(options));
};

type IMaskOptions = {
  padFractionalZeros?: boolean;
};

export function getPercentageMaskOption(options?: IMaskOptions): FactoryOpts {
  return {
    mask: [
      {mask: ''},
      {
        mask: 'num%',
        lazy: false,
        blocks: {
          num: {
            mask: Number,
            scale: 2,
            normalizeZeros: true,
            radix: '.',
            mapToRadix: ['.'],
            ...options,
          },
        },
      },
    ],
  };
}

export const percentageMask = (
  number: number | string,
  options?: IMaskOptions
): string => {
  if (typeof number === 'number') {
    number = String(number);
  }

  return IMask.pipe(number, getPercentageMaskOption(options));
};

export const unmaskPercentage = (percent: string): string => {
  const maskedPercent = createMask(getPercentageMaskOption());
  maskedPercent.resolve(percent);
  maskedPercent.unmaskedValue;

  return maskedPercent.unmaskedValue;
};

export const unmaskPrice = (price: string) => {
  const mask = createMask(priceMaskOption);

  mask.resolve(price);

  return mask.unmaskedValue;
};
